import React, { useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { UserGroup } from '../../../models';
import { NewUserGroupPage } from '.';
import { EntityChangeLog } from '../../reports';
import { TableNameColumn } from '../../../elements';

export function UserGroupsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    loadUserGroups();
  }, []);

  const loadUserGroups = () => {
    setLoading(true);
    apiClient
      .get('user-groups')
      .then(response => {
        setUserGroups(response.data as UserGroup[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadUserGroups();
  };

  const onUserGroupAdded = () => {
    closeAddModal();
    loadUserGroups();
  };

  const deleteUserGroup = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`user-groups/${id}`)
      .then(() => {
        loadUserGroups();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const confirmDeleteUserGroup = (id: string) => {
    const onOk = () => deleteUserGroup(id);
    Modal.confirm({
      title: 'Kullanıcı grubu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Kullanıcı grubunu silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteUserGroup(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, userGroup: UserGroup) => (
        <TableNameColumn name={name} description={userGroup.description} path={'/admin/user-groups/' + userGroup.id} />
      ),
    },
    {
      title: 'Üyeler',
      dataIndex: 'memberCount',
      key: 'memberCount',
      render: (memberCount: number) => (memberCount === 0 ? '-' : memberCount),
    },
    {
      title: 'Roller',
      dataIndex: 'roleCount',
      key: 'roleCount',
      render: (roleCount: number) => (roleCount === 0 ? '-' : roleCount),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, userGroup: UserGroup) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(userGroup.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <UsergroupAddOutlined /> Kullanıcı grupları
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
            Yeni kullanıcı grubu
          </Button>,
        ]}
      />
      <Content>
        <Table
          columns={tableColumns}
          rowKey={row => row.id}
          dataSource={userGroups}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={false}
        />
      </Content>
      <Modal title="Yeni kullanıcı grubu" visible={addModalOpen} footer={null} onCancel={closeAddModal} destroyOnClose={true}>
        <NewUserGroupPage onSaved={onUserGroupAdded} />
      </Modal>
      <Modal
        title="Kullanıcı değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'UserGroup'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
